import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import { isInvestor } from '../../global/authentication/authentication-manager.js'

import { getRelativeBasePath } from '../../global/environments/path.js'

import * as StringFormatter from '../../global/formatters/string-formatter.js'
import { removeSpecialCharacters } from '../../spa/utility/strings/strings.js'
import NoFundsFoundFooter from './NoFundsFoundFooter.jsx'

import {
  RETAIL_PRODUCTS,
  CLOSED_END_PRODUCTS,
  ETF_PRODUCTS,
  SEPARATELY_MANAGED_ACCOUNTS_PRODUCTS,
  FUTURE_SCHOLAR_529_PRODUCTS,
  VARIABLE_PRODUCTS,
  MODEL_PORTFOLIOS_PRODUCTS,
  INTERVAL_FUND_PRODUCTS,
  getProductPropertyByValue,
} from '../FundDetails/Helpers/FundTypes.js'

import { getProductListByCategory } from '../FundDetails/Data/FundDetailsDataSource.js'

import FundFinderFilterDropdown from './FundFinderFilterDropdown.jsx'
import CategoryItem from './CategoryItem.jsx'
import { lookupCategory } from '../../global/utilities/lookup-category.js'
import { basicSort } from '../../spa/utility/index.js'
import DOMPurify from 'dompurify'

export const PRODUCT_DISPLAY_ORDER = [
  'Mutual Funds',
  'Exchange Traded Funds (ETFs)',
  'Separately Managed Accounts',
  'Model Portfolios',
  '529 Plan',
  'Variable Products',
  'Interval Funds',
  'Listed Closed-End Funds',
]

export default function FundFinder({ onClose }) {
  const [fundData, setFundData] = useState([])
  const [products, setProducts] = useState([])
  const [selectedProduct, setSelectedProduct] = useState('all')
  const [classes, setClasses] = useState([])
  const [selectedClass, setSelectedClass] = useState('all')
  const [categories, setCategories] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])
  const [searchText, setSearchText] = useState('')
  const [fundFooterActive, setFundFooterActive] = useState(false)
  const [filteredFunds, setFilteredFunds] = useState([])

  useEffect(() => {
    loadProductData()
    handleSearch()
  }, [])

  useEffect(() => {
    filterFunds()
  }, [fundData, selectedClass, selectedProduct, selectedCategories, searchText])

  useEffect(() => {
    updateClassList()
  }, [filteredFunds])

  const loadProductData = async () => {
    const dataMF = async () => await getProductListByCategory(RETAIL_PRODUCTS)
    const dataETF = async () => await getProductListByCategory(ETF_PRODUCTS)
    const dataSMA = async () =>
      await getProductListByCategory(SEPARATELY_MANAGED_ACCOUNTS_PRODUCTS)
    const dataMP = async () =>
      await getProductListByCategory(MODEL_PORTFOLIOS_PRODUCTS)
    const dataFS = async () =>
      await getProductListByCategory(FUTURE_SCHOLAR_529_PRODUCTS)
    const dataCE = async () =>
      await getProductListByCategory(CLOSED_END_PRODUCTS)
    const dataVP = async () => await getProductListByCategory(VARIABLE_PRODUCTS)
    const dataIF = async () =>
      await getProductListByCategory(INTERVAL_FUND_PRODUCTS)

    try {
      const allFunds = await Promise.allSettled([
        dataMF(),
        dataETF(),
        dataSMA(),
        dataMP(),
        dataFS(),
        dataCE(),
        dataVP(),
        dataIF(),
      ])

      const _funds = flattenFunds(allFunds)

      const sortedFunds = _funds.sort(function (a, b) {
        if (a && b) {
          return a.fundName.localeCompare(b.fundName)
        } else {
          return 0
        }
      })

      const funds = []

      sortedFunds.forEach((fund) => {
        fund.classes.forEach((info) => {
          const newFund = {}
          newFund.fundName = fund.fundName
          newFund.fundGroup = info.fundGroup
          newFund.categoryName = fund.ctgName
          newFund.cusip = info.cusip
          newFund.symbol = info.symbol
          newFund.fundClass = info.fundClass

          funds.push(newFund)
        })
      })

      const parsedFunds = funds.reduce((prev, curr) => {
        let match = false

        prev?.forEach((item) => {
          if (item.cusip === curr.cusip) {
            match = true
          }
        })

        return !match ? [...prev, ...[curr]] : prev
      }, [])

      setFundData(parsedFunds)

      getProductListFromFunds(parsedFunds)

      getClassListFromFunds(parsedFunds)
    } catch (error) {
      console.error(error)
    }
  }

  const checkKeyMappingMatch = (key, value) => {
    return Array.isArray(key) ? key?.some((k) => k === value) : key === value
  }

  const flattenFunds = (data) => {
    const _data = data?.reduce(
      (prev, curr) => [...prev, ...curr.value.value.products],
      []
    )
    const _funds = _data?.reduce((prev, curr) => [...prev, ...curr.funds], [])

    return _funds
  }

  const getProductListFromFunds = (funds) => {
    const productList = funds.reduce((prev, curr) => {
      const productType = getProductPropertyByValue(
        'productUILabel',
        curr.fundGroup
      )
      return curr?.fundGroup &&
        !prev.includes(curr.fundGroup) &&
        !prev.includes(productType)
        ? [...prev, ...[productType]]
        : prev
    }, [])

    setProducts(basicSort(productList, PRODUCT_DISPLAY_ORDER, true))
  }

  const handleProductDropdownSelect = (productType) => {
    setSelectedProduct(productType)
    setSelectedClass('all')
    setSelectedCategories([])
    setCategories([])
  }

  const getClassListFromFunds = (funds) => {
    const classList = funds.reduce((prev, curr) => {
      return curr?.fundClass && !prev.includes(curr.fundClass)
        ? [...prev, ...[curr.fundClass]]
        : prev
    }, [])

    setClasses(classList.sort())
  }

  const updateClassList = () => {
    const _fundData = fundData.reduce((prev, curr) => {
      return curr.fundClass &&
        !prev.includes(curr.fundClass) &&
        (checkKeyMappingMatch(
          getProductPropertyByValue('productUILabel', selectedProduct),
          curr.fundGroup
        ) ||
          selectedProduct === 'all')
        ? [...prev, ...[curr.fundClass]]
        : prev
    }, [])

    setClasses(_fundData)
  }

  const getCategoriesListFromFunds = (funds) => {
    const _funds = [...funds]
    const fundsWithAssetClass = _funds.filter(
      (fund) =>
        fund.fundGroup === 'Retail' ||
        fund.fundGroup === 'Retail ETF' ||
        fund.fundGroup === 'Separately Managed Accounts'
    )

    const categoriesList = fundsWithAssetClass.reduce((prev, curr) => {
      return curr?.categoryName &&
        !prev.includes(curr.categoryName) &&
        (checkKeyMappingMatch(
          getProductPropertyByValue('productUILabel', selectedProduct),
          curr.fundGroup
        ) ||
          selectedProduct === 'all')
        ? [...prev, ...[curr.categoryName]]
        : prev
    }, [])

    const _categoriesList = [...categoriesList].sort(
      (a, b) => lookupCategory(a).order - lookupCategory(b).order
    )

    setCategories(_categoriesList)
  }

  const handleSearch = () => {
    document
      .getElementById('search-text-input')
      ?.addEventListener('input', (event) => {
        setFundFooterActive(true)
        setSearchText(event.target.value)
      })
  }

  const handleCategoryToggle = (category, isChecked) => {
    setSelectedCategories((prev) =>
      isChecked ? [...prev, category] : prev.filter((item) => item !== category)
    )
  }

  const filterFunds = () => {
    const filteredFunds = fundData.filter((fund) => {
      const fundName = '' + fund.fundName
      const symbol = '' + fund.symbol
      const cusip = '' + fund.cusip
      let filteredCheckBox = true // by default should be true so all funds match
      let match = true

      // Match by Product Type
      match =
        match &&
        (checkKeyMappingMatch(
          getProductPropertyByValue('productUILabel', selectedProduct),
          fund.fundGroup
        ) ||
          selectedProduct === 'all')
      // match && (fund.fundGroup === selectedProduct || selectedProduct === 'all')

      // Match by fundClass
      match =
        match && (fund.fundClass == selectedClass || selectedClass == 'all')

      // Match by fundCategory
      if (selectedCategories.length > 0) {
        filteredCheckBox = false

        selectedCategories.forEach((item) => {
          if (fund.categoryName == item && hasASsetClassCategories(fund)) {
            filteredCheckBox = true
          }
        })
      }

      match = match && filteredCheckBox

      // Match by search input
      if (searchText.length > 0) {
        const lowerCaseSearchText = searchText.toLowerCase()

        match =
          (match && fundName.toLowerCase().includes(lowerCaseSearchText)) ||
          (match && symbol.toLowerCase().includes(lowerCaseSearchText)) ||
          (match && cusip.toLowerCase().includes(lowerCaseSearchText))
      }

      return match
    })

    setFilteredFunds(filteredFunds)

    getCategoriesListFromFunds(fundData)
  }

  const getFundClass = (fundClass) => {
    const _fundClass = fundClass?.replace('Class ', '')
    return fundClass === null ? 'Class-None' : `Class-${_fundClass}`
  }

  const getFundUrlFromFund = (fund) => {
    return `${getRelativeBasePath()}/investment-products/${getProductPropertyByValue(
      'path',
      fund.fundGroup
    )}/${StringFormatter.urlSafe(removeSpecialCharacters(fund.fundName))}/${getFundClass(
      fund.fundClass
    )}/details/?cusip=${fund.cusip}`
  }

  const hasASsetClassCategories = (fund) => {
    return (
      fund.fundGroup === 'Retail' ||
      fund.fundGroup === 'Retail ETF' ||
      fund.fundGroup === 'Separately Managed Accounts' ||
      fund.fundGroup === 'Retail and Institutional'
    )
  }

  const renderTable = (funds) => {
    const _funds = funds || fundData

    if (_funds.length > 0) {
      return _funds.map((fund) => {
        return (
          <li
            className="u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0 u-paddingLeft3gu u-paddingVert0"
            key={fund.cusip}
          >
            <a
              className="u-flex u-flexAlignItemsCenter u-flexJustifyBetween"
              href={DOMPurify.sanitize(getFundUrlFromFund(fund))}
            >
              <span className="Grid Grid--alignMiddle Grid--fit u-marginVert3gu">
                <span className="u-alignMiddle">
                  <h4 className="u-textColorBrand">
                    {fund.fundName}{' '}
                    {fund.fundClass ? `- ${fund.fundClass}` : ''}
                  </h4>
                  <p className="u-textNormal u-textSizeMsrp1 u-textPrimary">
                    {fund.symbol}
                  </p>
                </span>
              </span>
              <span className="adel-material-symbols Grid--alignRight u-paddingRight3gu u-textSizeMsrp4 width-auto">
                chevron_right
              </span>
            </a>
          </li>
        )
      })
    } else {
      return (
        <li className="u-paddingHoriz3gu u-paddingVert2gu">
          <p>No matching funds found.</p>
        </li>
      )
    }
  }

  return (
    <div className="Search-listModule mutual-fund-finder-table-container">
      <div className="u-sm-block u-lg-flex u-flexAlignItemsCenter u-decoBackgroundWhite">
        <span
          className={`${
            isInvestor() ? 'u-size4of12' : 'u-size3of12'
          } u-bgColorBrandDark u-paddingHoriz2gu u-paddingTop3gu u-sm-hidden u-md-hidden`}
        >
          <label
            className="Dropdown-label Dropdown-label--reversed u-inlineBlock u-paddingRight2gu u-marginBottom2gu"
            data-collapsible="true"
            htmlFor="dropdown-menu"
          >
            <h2 className="u-textColorWhite">Select Product Type:</h2>
          </label>
          <FundFinderFilterDropdown
            filterType={products}
            filter={'category'}
            onChangeHandler={handleProductDropdownSelect}
            classList={
              'Dropdown Dropdown--reversed fund-className-dropdown u-inlineBlock u-marginBottom2gu'
            }
          />
        </span>
        <div
          className="u-size9of12 u-md-sizeFull u-sm-sizeFull"
          id="mutual-fund-finder-table"
        >
          <label htmlFor="input-name" className="u-hiddenVisually">
            Find a Product
          </label>
          <div className="Input-group--flex">
            <span className="adel-material-symbols u-flexAlignSelfCenter u-marginHoriz3gu u-textSizeMsrp3 Search--icon">
              search
            </span>
            <input
              id="search-text-input"
              type="text"
              name="input-name"
              className="Input u-border0 u-padding0 u-textSecondary u-textSizeMsrp2"
              placeholder="FIND A PRODUCT"
            ></input>
            <button
              id="close-fund-finder"
              className="adel-material-symbols u-textColorBrand u-textSizeMsrp4 Close--icon u-flexAlignSelfCenter u-marginHoriz3gu"
              onClick={onClose}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Escape') {
                  onClose(e)
                }
              }}
            >
              <span className="adel-material-symbols u-sizeAuto Close--icon">
                close
              </span>
            </button>
          </div>
        </div>
      </div>
      <hr className="Rule u-textColorBrandLight" />
      <div className="u-lg-flex Search-listMain">
        <div
          className={`${
            isInvestor() ? 'u-size4of12' : 'u-size3of12'
          } u-bgColorBrandDark u-paddingHoriz3gu u-paddingVert3gu u-md-hidden u-sm-hidden`}
        >
          {(classes?.length > 0 || categories?.length > 0) && (
            <h2 className="u-textColorWhite u-marginVert2gu">Filter By:</h2>
          )}
          {classes?.length > 0 && (
            <span
              className={`${
                isInvestor() ? 'u-size4of12' : 'u-size3of12'
              } u-bgColorBrandDark u-sm-hidden u-md-hidden`}
            >
              <label
                className="Dropdown-label Dropdown-label--reversed u-inlineBlock u-paddingRight2gu u-paddingBottom5gu"
                data-collapsible="true"
                htmlFor="dropdown-menu"
              >
                <h3 className="u-textColorWhite">Share Class:</h3>
              </label>
              <FundFinderFilterDropdown
                filterType={classes}
                filter={'class'}
                onChangeHandler={setSelectedClass}
                classList={
                  'Dropdown Dropdown--reversed fund-className-dropdown u-inlineBlock'
                }
              />
            </span>
          )}
          {categories?.length > 0 && (
            <span
              className={`${
                isInvestor() ? 'u-size4of12' : 'u-size3of12'
              } u-bgColorBrandDark u-sm-hidden u-md-hidden`}
            >
              <h3 className="u-textColorWhite">Asset Class:</h3>
            </span>
          )}
          <ul id="mutual-fund-finder-category-list" className="u-paddingTop4gu">
            {(selectedProduct === 'Mutual Funds' ||
              selectedProduct === 'Exchange Traded Funds (ETFs)' ||
              selectedProduct === 'Separately Managed Accounts' ||
              selectedProduct === 'all') &&
              categories?.map((category) => {
                return (
                  <CategoryItem
                    category={category}
                    key={category}
                    checked={selectedCategories.includes(category)}
                    onChange={handleCategoryToggle}
                  />
                )
              })}
          </ul>
        </div>
        <div className="Search-listResults u-posRelative u-sm-size12of12 u-md-size12of12 u-lg-size9of12 u-decoBackgroundWhite">
          <ul className="u-overflow" id="mutual-fund-finder-list">
            {filteredFunds && renderTable(filteredFunds)}
          </ul>
          {fundFooterActive && <NoFundsFoundFooter />}
        </div>
      </div>
    </div>
  )
}

FundFinder.propTypes = {
  onClose: PropTypes.func.isRequired,
}
